/**
 * The on-device player probe (PLAYER-2 / PLAYER-4) - the part that thinks.
 *
 * `docs/player-hardware-tests.md` asks a person to stand at a panel for twenty
 * minutes per test, place content by hand, watch a wall, pull a log capture, and
 * then ASSEMBLE a verdict out of five outcomes that look identical from across a
 * room. This module is that assembly, moved onto the panel: the screen
 * (`PlayerProbeScreen.tsx`) drives a real decoder, and everything here decides
 * what the results MEAN.
 *
 * ⚠️ The screen is `PlayerProbeScreen.tsx`, not `PlayerProbe.tsx`, and that is
 * deliberate: a `PlayerProbe.tsx` sitting beside this `playerProbe.ts` differs
 * only in case, and on a case-insensitive filesystem (macOS) the resolver
 * answers `./PlayerProbe` with THIS file - so the component silently imports
 * itself, every export reads `undefined`, and React reports "element type is
 * invalid" from somewhere else entirely. Do not rename it back.
 *
 * ## Why the judging is a separate, pure module
 *
 * The interesting half of both tests is not "did it play" - it is the table that
 * separates the ways a black screen happens. That table has no decoder in it. It
 * is a function from (played?, failure code, parked playhead, duration) to a
 * sentence, and a function is testable without a panel, without Shaka, and
 * without EME. So it lives here, gets pinned by `playerProbe.test.ts`, and the
 * component is left with nothing but lifecycle.
 *
 * ## The three rules this module inherits
 *
 *  1. **The named failure IS the finding.** Four of the five ways TEST 1 goes
 *     black are not about DRM at all. Reading "it did not play" as "no Widevine"
 *     would retire a whole device class over a wrong manifest URL, so every
 *     verdict here is keyed on the `StreamFailure` code that
 *     `streamPlayback.ts` already produces - never on a fresh vocabulary.
 *  2. **Never echo a licence token.** Licence URLs are ES256 JWTs riding in a
 *     query string. Everything that leaves this module goes through
 *     {@link redactLicenceUrl} first, because the report is printed on a
 *     television, photographed, and pasted into a ticket.
 *  3. **"unknown" is an answer.** Widevine security level is not exposed on
 *     every platform. Saying so is a finding; guessing L3 is a lie that would be
 *     quoted back at us in a programme decision.
 *
 * PlayReady is not in scope and is not a question (ruled by Sigge; PLAYER-3
 * closed on it). If a test here fails, the answer is never "try PlayReady".
 */

import type { StreamFailure } from "../schemes/_shared/streamPlayback";

// ---------------------------------------------------------------------------
// The plan: what to play. Fetched, with a default that always works.
// ---------------------------------------------------------------------------

/**
 * What a ladder rung is for, which is what its failure MEANS.
 *
 * `docs/player-hardware-tests.md` reads the ladder by row number ("stops at 3 -
 * a profile ceiling"). Row numbers do not survive a plan served from a route, so
 * the meaning is carried on the rung itself and the reading below is keyed on
 * it. One variable changes per rung; this names which one.
 */
export type RungTell =
  /** Does this panel decode through `<video>` AT ALL. */
  | "html_path"
  /** The fleet's normal encode (Main@4.0, 4 Mbps). */
  | "fleet_normal"
  /** Profile held against, bitrate held: High vs Main. */
  | "profile"
  /** Profile held, bitrate raised: finds the number the ABR cap sits below. */
  | "bitrate"
  /** 4K, and only on a panel that claims it. */
  | "4k";

export interface LadderRung {
  id: string;
  /** Ten-foot legible, one line. */
  label: string;
  /** Empty string means "no clip supplied" - reported, never faked. */
  url: string;
  resolution?: string;
  profile?: string;
  bitrateMbps?: number;
  /** Which single variable this rung moves. */
  tell: RungTell;
  /** Skip unless the panel actually claims 4K. */
  requires4k?: boolean;
  /** How long the playhead must keep moving before this rung is a pass. */
  sustainMs?: number;
}

export interface ProbePlan {
  /** Suite A: one encrypted DASH manifest, and where its licences come from. */
  drm: {
    manifestUrl: string;
    licenceUrl: string;
    label: string;
    /**
     * How long to hold a licensed stream open looking for a mid-play expiry.
     * 0 = skipped, and SAID to be skipped. A Verimatrix token lives five
     * minutes, so a real expiry test costs six - too long to run by default,
     * and a probe that silently ran for six minutes would be abandoned before
     * it finished.
     */
    expiryWatchMs: number;
  };
  /** Suite B: the decode ladder, one variable per rung. */
  ladder: LadderRung[];
  /** Suite C: the decoder-release soak. */
  soak: { cycles: number; url: string; watchMs: number };
}

/** Where the probe's clips live when nobody has said otherwise. */
const CLIP_BASE = "/foyer-assets/player-probe";

/**
 * The fallback plan.
 *
 * A sibling unit owns the route that hands this probe a real rendition ladder
 * and records the verdict. That route may not exist yet, may 404 on an older
 * API, and will certainly not exist on a panel pointed at a preview host - so
 * the probe carries a plan of its own and says which one it used. A diagnostic
 * that only works once the rest of the system is finished is not a diagnostic.
 *
 * The DRM row is Shaka's own public Widevine test pair. Deliberately not a
 * Foyer title: TEST 1 asks whether the WEBVIEW can open encrypted DASH, and
 * answering it must not first require a correctly packaged catalogue entry, a
 * premise, a playlist and a licence server. A hotel network that blocks it
 * reports `manifest_unreachable`, which the table below already separates from
 * "no CDM" - and the plan route can point it at anything else.
 *
 * The ladder rows are the five in `docs/player-hardware-tests.md`, by the file
 * names that doc tells whoever prepares the clips to upload. A missing clip
 * reports `no_clip`, never a decode verdict.
 */
export const DEFAULT_PROBE_PLAN: ProbePlan = {
  drm: {
    label: "Shaka Widevine test asset (angel-one)",
    manifestUrl:
      "https://storage.googleapis.com/shaka-demo-assets/angel-one-widevine/dash.mpd",
    licenceUrl: "https://cwip-shaka-proxy.appspot.com/no_auth",
    expiryWatchMs: 0,
  },
  ladder: [
    {
      id: "rung1",
      label: "1280×720 · Constrained Baseline 3.1 · 1.5 Mbps",
      url: `${CLIP_BASE}/rung1-720p-cbp31-1500k.mp4`,
      resolution: "1280×720",
      profile: "Constrained Baseline 3.1",
      bitrateMbps: 1.5,
      tell: "html_path",
    },
    {
      id: "rung2",
      label: "1920×1080 · Main 4.0 · 4 Mbps (the fleet's normal encode)",
      url: `${CLIP_BASE}/rung2-1080p-main40-4000k.mp4`,
      resolution: "1920×1080",
      profile: "Main 4.0",
      bitrateMbps: 4,
      tell: "fleet_normal",
    },
    {
      id: "rung3",
      label: "1920×1080 · High 4.2 · 8 Mbps (the first real question)",
      url: `${CLIP_BASE}/rung3-1080p-high42-8000k.mp4`,
      resolution: "1920×1080",
      profile: "High 4.2",
      bitrateMbps: 8,
      tell: "profile",
    },
    {
      id: "rung4",
      label: "1920×1080 · Main 4.0 · 12 Mbps (profile held, bitrate raised)",
      url: `${CLIP_BASE}/rung4-1080p-main40-12000k.mp4`,
      resolution: "1920×1080",
      profile: "Main 4.0",
      bitrateMbps: 12,
      tell: "bitrate",
    },
    {
      id: "rung5",
      label: "3840×2160 · Main 5.1 · 15 Mbps (only if the panel claims 4K)",
      url: `${CLIP_BASE}/rung5-2160p-main51-15000k.mp4`,
      resolution: "3840×2160",
      profile: "Main 5.1",
      bitrateMbps: 15,
      tell: "4k",
      requires4k: true,
    },
  ],
  soak: {
    cycles: 50,
    url: `${CLIP_BASE}/rung1-720p-cbp31-1500k.mp4`,
    watchMs: 400,
  },
};

/** How long a rung's playhead must keep moving before the rung is a pass. */
const DEFAULT_SUSTAIN_MS = 12_000;

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function num(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

const RUNG_TELLS: RungTell[] = [
  "html_path",
  "fleet_normal",
  "profile",
  "bitrate",
  "4k",
];

/**
 * Read a plan off whatever the route actually returned.
 *
 * TOLERANT ON PURPOSE, in both directions. A sibling unit owns that route, so
 * this must survive it not existing (404 → caller passes `undefined`), existing
 * in a different shape, or being an HTML error page that parsed into something
 * that is not an object. Every field falls back to the default independently,
 * so a plan that supplies only a ladder still gets a DRM asset, and one bad rung
 * cannot take the other four with it.
 */
export function parseProbePlan(raw: unknown): ProbePlan {
  if (!raw || typeof raw !== "object") return DEFAULT_PROBE_PLAN;
  const src = raw as Record<string, unknown>;

  const drmRaw = (src.drm ?? {}) as Record<string, unknown>;
  const drm: ProbePlan["drm"] = {
    manifestUrl: str(drmRaw.manifestUrl) ?? DEFAULT_PROBE_PLAN.drm.manifestUrl,
    licenceUrl: str(drmRaw.licenceUrl) ?? DEFAULT_PROBE_PLAN.drm.licenceUrl,
    label: str(drmRaw.label) ?? DEFAULT_PROBE_PLAN.drm.label,
    expiryWatchMs:
      num(drmRaw.expiryWatchMs) ?? DEFAULT_PROBE_PLAN.drm.expiryWatchMs,
  };

  const ladderRaw = Array.isArray(src.ladder) ? src.ladder : [];
  const ladder = ladderRaw
    .map((entry, i): LadderRung | null => {
      if (!entry || typeof entry !== "object") return null;
      const r = entry as Record<string, unknown>;
      const url = str(r.url);
      if (!url) return null;
      const tell = r.tell as RungTell;
      return {
        id: str(r.id) ?? `rung${i + 1}`,
        label: str(r.label) ?? url,
        url,
        resolution: str(r.resolution),
        profile: str(r.profile),
        bitrateMbps: num(r.bitrateMbps),
        tell: RUNG_TELLS.includes(tell) ? tell : "bitrate",
        requires4k: r.requires4k === true,
        sustainMs: num(r.sustainMs),
      };
    })
    .filter((r): r is LadderRung => r !== null);

  const soakRaw = (src.soak ?? {}) as Record<string, unknown>;
  const soak: ProbePlan["soak"] = {
    // Clamped to at least one cycle. A plan that said `0` would run no cycles,
    // complete "all" of them, and print a PASS on the leak class - a green tick
    // for a suite that never ran is worse than no suite.
    cycles: Math.max(
      1,
      Math.round(num(soakRaw.cycles) ?? DEFAULT_PROBE_PLAN.soak.cycles)
    ),
    url: str(soakRaw.url) ?? ladder[0]?.url ?? DEFAULT_PROBE_PLAN.soak.url,
    watchMs: num(soakRaw.watchMs) ?? DEFAULT_PROBE_PLAN.soak.watchMs,
  };

  return {
    drm,
    ladder: ladder.length > 0 ? ladder : DEFAULT_PROBE_PLAN.ladder,
    soak,
  };
}

// ---------------------------------------------------------------------------
// Secrets
// ---------------------------------------------------------------------------

/**
 * A licence URL with its credential removed.
 *
 * The token is an ES256 JWT in the query string. This page is printed on a
 * television in a public room, photographed, pasted into tickets, and forwarded
 * through `log_capture` - so the query never leaves this function. The origin
 * and path DO leave it, because "which licence server did it ask" is exactly the
 * fact that separates a misconfiguration from a refusal.
 *
 * Never throws: a value that is not a URL at all is reported as
 * `(unparseable)`, not printed raw, because the reason it is unparseable might
 * be that it is a bare token.
 */
export function redactLicenceUrl(url: string | null | undefined): string {
  if (!url) return "(none)";
  // Anything that is neither absolute nor rooted is not a URL, and the most
  // likely reason a "licence URL" is not a URL is that it is a BARE TOKEN.
  // Parsing it against a base would turn the credential into a path and print
  // it, which is the exact accident this function exists to prevent.
  const absolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);
  const rooted = url.startsWith("/");
  if (!absolute && !rooted) return "(unparseable)";
  try {
    const u = new URL(url, "https://relative.invalid");
    const had = u.search.length > 1 || u.hash.length > 1;
    return `${absolute ? u.origin : ""}${u.pathname}${had ? "?<redacted>" : ""}`;
  } catch {
    return "(unparseable)";
  }
}

/**
 * A last line of defence over every free-text string the probe prints.
 *
 * `redactLicenceUrl` handles the URLs we hand around ourselves. This handles the
 * ones we do not control: a player error message, a rejected fetch, a stack
 * frame - any of which can carry the licence URL, and therefore the JWT, that
 * the request was made with. A three-segment base64url run is a JWT and there is
 * no legitimate reason for one to appear on a diagnostic screen.
 */
export function scrubSecrets(text: string): string {
  return text.replace(
    /[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}/g,
    "<redacted-token>"
  );
}

// ---------------------------------------------------------------------------
// Widevine security level
// ---------------------------------------------------------------------------

export type SecurityLevel = "L1" | "L2" | "L3" | "unknown";

/**
 * EME robustness strings, strongest first, and the Widevine level each implies.
 *
 * There is no API that says "L1". What there IS, is a request for a robustness
 * the CDM either grants or refuses, and the strongest grant names the level. So
 * the probe asks down the list and reports the first yes - a capability probe,
 * exactly like `drmSupported()`, and never a user-agent guess.
 *
 * A platform that grants an EMPTY robustness and refuses all of these is real
 * (older WebViews accept `""` and nothing else). That is `unknown`, and it is
 * reported as unknown.
 */
export const WIDEVINE_ROBUSTNESS: Array<{
  robustness: string;
  level: SecurityLevel;
}> = [
  { robustness: "HW_SECURE_ALL", level: "L1" },
  { robustness: "HW_SECURE_DECODE", level: "L1" },
  { robustness: "HW_SECURE_CRYPTO", level: "L2" },
  { robustness: "SW_SECURE_DECODE", level: "L3" },
  { robustness: "SW_SECURE_CRYPTO", level: "L3" },
];

/** The level a granted robustness string implies. `unknown` for anything else. */
export function levelForRobustness(robustness: string): SecurityLevel {
  return (
    WIDEVINE_ROBUSTNESS.find((r) => r.robustness === robustness)?.level ??
    "unknown"
  );
}

/**
 * What the security level means for the programme, said plainly.
 *
 * ⚠️ A NO here is a PROGRAMME-LEVEL DECISION POINT, not a bug to fix. If a
 * device class has no Widevine, no amount of player work changes it: VOD and
 * live TV stay in the vendor app on that hardware. Whoever reads this off a
 * panel must not go looking for a code fix.
 */
export function judgeSecurityLevel(
  level: SecurityLevel,
  widevineSupported: boolean
): { ok: boolean; note: string } {
  if (!widevineSupported) {
    return {
      ok: false,
      note:
        "No Widevine on this device. This is a PROGRAMME-LEVEL DECISION POINT, " +
        "not a bug: this device class cannot carry protected content through " +
        "the WebView, and no player work changes that.",
    };
  }
  if (level === "unknown") {
    return {
      ok: true,
      note:
        "Widevine answered, but this platform does not disclose a security " +
        "level. Recorded as unknown rather than guessed.",
    };
  }
  if (level === "L1") {
    return {
      ok: true,
      note: "L1 - hardware-backed. Studio HD/UHD tiers are open on this panel.",
    };
  }
  return {
    ok: true,
    note: `${level} - software-backed. Playable; expect SD-only entitlements from studios that tier on robustness.`,
  };
}

// ---------------------------------------------------------------------------
// Suite A - the five ways a black screen happens
// ---------------------------------------------------------------------------

/** The verdict column of TEST 1's table, as a value rather than a sentence. */
export type DrmVerdict =
  /** PLAYER-2 is a yes on this hardware. */
  | "yes"
  /** The CDM is absent or will not take this key system. THIS is the no. */
  | "no"
  /** It went black for a reason that is not about DRM capability at all. */
  | "elsewhere"
  /** The player never started - no failure code was produced at all. */
  | "never_started";

export interface DrmDiagnosis {
  verdict: DrmVerdict;
  /** The "What it means" column. */
  meaning: string;
  /** The "Next" column. */
  next: string;
}

/**
 * TEST 1's table, keyed by the failure code the player already names.
 *
 * ⚠️ **Do not read "it did not play" as "no DRM".** Four of the five documented
 * rows are not about DRM at all, and the code is what separates them. That
 * distinction is the whole reason `streamPlayback.ts` names its failures instead
 * of showing a spinner, and it is the reason this probe exists rather than a
 * person watching a wall.
 *
 * The wording is lifted from `docs/player-hardware-tests.md` deliberately: a
 * parallel vocabulary invented here would mean the panel and the runbook
 * disagree about what the same black screen was.
 */
export function diagnoseDrm(
  played: boolean,
  failure: StreamFailure | null
): DrmDiagnosis {
  if (played) {
    return {
      verdict: "yes",
      meaning: "PLAYER-2 is a yes on this hardware. Record model + firmware.",
      next: "Nothing",
    };
  }
  switch (failure) {
    case "codec_unsupported":
      return {
        verdict: "no",
        meaning: "The CDM is absent or will not take this key system",
        next: "THIS IS THE NO. Record it - it decides the platform",
      };
    case "licence_refused":
      return {
        verdict: "elsewhere",
        meaning:
          "The panel asked and was refused. Key, entitlement or content id.",
        next: "Check VERIMATRIX_* on nenda-foyer-prod; check the title's content id",
      };
    case "licence_expired":
      return {
        verdict: "elsewhere",
        meaning:
          "A licence was held and expired, and a fresh one could not be had. The CDM worked.",
        next: "Check the token TTL and that the licence URL is minted per request",
      };
    case "manifest_unreachable":
      return {
        verdict: "elsewhere",
        meaning: "Delivery, not DRM. The panel never got the manifest",
        next: "Check the URL resolves from the hotel network",
      };
    case "decoder_exhausted":
      return {
        verdict: "elsewhere",
        meaning:
          "No decoder was free. A leak upstream of this attempt, not a DRM answer.",
        next: "Read SUITE C below - and reboot the panel before re-running TEST 1",
      };
    case "network_stalled":
      return {
        verdict: "elsewhere",
        meaning:
          "It started and then stopped with bytes still owed. Delivery, not rights.",
        next: "Check the uplink from this room, then re-run",
      };
    case "unknown":
      return {
        verdict: "elsewhere",
        meaning:
          "The player failed with something the classifier could not place.",
        next: "Read the detail column, then pull a log capture",
      };
    default:
      return {
        verdict: "never_started",
        meaning: "The player never started",
        next: "Check the screen is on the new bundle (Devices → Web bundle)",
      };
  }
}

/**
 * The withheld-licence attempt, judged.
 *
 * 🚨 **This is a security assertion wearing a diagnostic's clothes.** Encrypted
 * content that plays with NO licence configured means the panel decrypted
 * nothing and fell back to clear - the content is unprotected on that hardware
 * and every rights holder has to be told. A refusal here is a PASS.
 */
export function judgeWithheld(attempt: {
  played: boolean;
  failure: StreamFailure | null;
}): { pass: boolean; note: string } {
  if (attempt.played) {
    return {
      pass: false,
      note:
        "🚨 IT PLAYED WITH NO LICENCE. The panel fell back to clear rather than " +
        "refusing. Protected content is NOT protected on this hardware - " +
        "record the model and stop placing protected titles on it.",
    };
  }
  if (attempt.failure === null) {
    return {
      pass: false,
      note:
        "It did not play, but produced no failure code either - so this says " +
        "nothing about the refusal. Re-run; if it repeats, the player never started.",
    };
  }
  return {
    pass: true,
    note: `Refused with ${attempt.failure}, as it must. Failing here is the correct behaviour, not a bug.`,
  };
}

// ---------------------------------------------------------------------------
// Suite B - the decode ladder, and the tell that costs a day
// ---------------------------------------------------------------------------

export type RungOutcome =
  /** Played, sustained, playhead still moving at the end of the watch. */
  | "plays"
  /** Started and then froze with time left. */
  | "stalls"
  /** Parked at ~0s: the decoder refused the clip. */
  | "never_started"
  /** Parked at the DURATION: it decoded fine and did not loop. */
  | "loop_failure"
  /** The clip itself could not be fetched. NOT a decode result. */
  | "no_clip"
  /** Not applicable to this panel (a 4K rung on a 1080 panel). */
  | "skipped";

/**
 * How close to 0 the playhead has to park before it counts as a decode refusal.
 * The measured LFD result parked at 0.1s across three separate encodes.
 */
export const NEVER_STARTED_SECONDS = 0.5;

/** How close to the duration counts as "played to the end and stopped there". */
export const AT_DURATION_SECONDS = 0.5;

export interface RungSample {
  played: boolean;
  /** Was the playhead still advancing when the watch window closed? */
  sustained: boolean;
  parkedAtSeconds: number | null;
  durationSeconds: number | null;
  failure: StreamFailure | null;
}

/**
 * Judge one rung.
 *
 * ⚠️ **THE TELL THAT COST A DAY.** A clip that never decodes and a clip that
 * plays but will not loop look IDENTICAL on a wall - both are a still picture
 * that stopped. The only difference is where the playhead parked: a decode
 * refusal parks at 0.0-0.1s, a loop failure parks at the clip's duration. So
 * the parked position is reported for every rung, and the outcome is derived
 * from it rather than from what the screen looked like.
 */
export function judgeRung(s: RungSample): {
  outcome: RungOutcome;
  note: string;
} {
  const at =
    s.parkedAtSeconds === null ? "?" : `${s.parkedAtSeconds.toFixed(1)}s`;
  const dur =
    s.durationSeconds === null || !Number.isFinite(s.durationSeconds)
      ? "?"
      : `${s.durationSeconds.toFixed(1)}s`;

  if (s.failure === "manifest_unreachable") {
    return {
      outcome: "no_clip",
      note: "The clip could not be fetched. NOT a decode verdict - upload it, or point the plan route at one that exists.",
    };
  }
  if (s.failure === "decoder_exhausted") {
    return {
      outcome: "never_started",
      note: `No decoder was free (parked ${at}). Read SUITE C: an earlier rung leaked one.`,
    };
  }
  if (s.failure === "codec_unsupported") {
    return {
      outcome: "never_started",
      note: `The panel refused this codec outright (parked ${at} of ${dur}).`,
    };
  }
  if (
    s.parkedAtSeconds !== null &&
    s.durationSeconds !== null &&
    Number.isFinite(s.durationSeconds) &&
    s.durationSeconds > 0 &&
    s.parkedAtSeconds >= s.durationSeconds - AT_DURATION_SECONDS
  ) {
    return {
      outcome: "loop_failure",
      note: `Parked at the DURATION (${at} of ${dur}). It decoded fine and did not loop - this is NOT a decode ceiling.`,
    };
  }
  if (!s.played || (s.parkedAtSeconds ?? 0) <= NEVER_STARTED_SECONDS) {
    return {
      outcome: "never_started",
      note: `Parked at ${at} of ${dur} - a decode refusal, not a loop failure.`,
    };
  }
  if (s.sustained) {
    return {
      outcome: "plays",
      note: `Sustained; playhead still moving at ${at} of ${dur}.`,
    };
  }
  return {
    outcome: "stalls",
    note: `Started, then stuck at ${at} of ${dur} - it played what it buffered and could not keep up.`,
  };
}

export interface LadderRow extends LadderRung {
  outcome: RungOutcome;
  note: string;
  parkedAtSeconds: number | null;
  durationSeconds: number | null;
  startupMs: number | null;
  failure: StreamFailure | null;
  detail: string;
}

export interface CeilingVerdict {
  /** The highest rung that played sustained, if any. */
  highestPassed: string | null;
  /** The first rung that did not, ignoring skips and missing clips. */
  firstFailure: string | null;
  meaning: string;
  /**
   * The bitrate the ABR cap must sit at or below - the highest that played.
   * Null when nothing played, or when no passing rung declared one.
   */
  abrCapMbps: number | null;
}

/**
 * Read the ladder, per `docs/player-hardware-tests.md` § "Reading it".
 *
 * The meaning is keyed on WHICH variable the first failing rung moved, not on
 * its row number: a plan served from a route can reorder or extend the ladder,
 * and a reading that counts rows would then be quietly wrong.
 */
export function readLadder(rows: LadderRow[]): CeilingVerdict {
  const ran = rows.filter(
    (r) => r.outcome !== "skipped" && r.outcome !== "no_clip"
  );
  const passed = ran.filter((r) => r.outcome === "plays");
  const failed = ran.filter(
    (r) => r.outcome === "never_started" || r.outcome === "stalls"
  );
  const first = failed[0] ?? null;
  const capCandidates = passed
    .map((r) => r.bitrateMbps)
    .filter((b): b is number => typeof b === "number");
  const abrCapMbps = capCandidates.length ? Math.max(...capCandidates) : null;

  if (ran.length === 0) {
    return {
      highestPassed: null,
      firstFailure: null,
      meaning:
        "No rung actually ran - every clip was missing or skipped. This is not a decode result.",
      abrCapMbps: null,
    };
  }
  if (!first) {
    return {
      highestPassed: passed[passed.length - 1]?.id ?? null,
      firstFailure: null,
      meaning:
        "Reached the top of the ladder. Record it - but do not plan 4K signage on one panel's result.",
      abrCapMbps,
    };
  }

  const meaning: Record<RungTell, string> = {
    html_path:
      "This panel CANNOT USE THE HTML PATH. That is the Samsung LFD result, and it means AVPlay (Tizen) or a native surface - not a re-encode.",
    fleet_normal:
      "The fleet's normal encode does not play here. Treat as the HTML path being unusable in practice, and re-check with rung 1 before concluding.",
    profile: "A PROFILE ceiling. Encode Main, never High. Cheap to live with.",
    bitrate:
      "A BITRATE ceiling. This is the number the ABR cap must be set below, and the single most useful figure this test produces.",
    "4k": "4K did not play. Record it; do not plan 4K signage on one panel's result either way.",
  };

  return {
    highestPassed: passed[passed.length - 1]?.id ?? null,
    firstFailure: first.id,
    meaning: meaning[first.tell],
    abrCapMbps,
  };
}

// ---------------------------------------------------------------------------
// Suite C - the decoder-release soak
// ---------------------------------------------------------------------------

export interface SoakSample {
  cyclesRequested: number;
  cyclesCompleted: number;
  /**
   * How many cycles actually reached a frame.
   *
   * The one that stops this suite lying. A soak whose clip 404s runs every
   * cycle, exhausts nothing, and would otherwise print PASS on the exact leak
   * class it exists to detect - a green tick from a run in which no decoder was
   * ever acquired.
   */
  cyclesPlayed: number;
  /** How many cycles came back `decoder_exhausted`. One is already a failure. */
  decoderExhausted: number;
  /** Any other named failure, counted by code. */
  otherFailures: Partial<Record<StreamFailure, number>>;
  heapStartBytes: number | null;
  heapEndBytes: number | null;
}

export interface SoakVerdict {
  pass: boolean;
  note: string;
}

/** Heap growth past this multiple, over the soak, is worth printing. */
const HEAP_GROWTH_FACTOR = 1.5;
/** …but only when the absolute growth is meaningful, not 3 MB of noise. */
const HEAP_GROWTH_FLOOR_BYTES = 48 * 1024 * 1024;

/**
 * Judge the soak.
 *
 * This is the signage leak class (`panel-video-encoding`: a pooled decoder,
 * single digits of instances, and an Android WebView that does NOT release one
 * when the `<video>` leaves the DOM) in a surface that starts and stops far more
 * often than a signage wall does. A single `decoder_exhausted` is the failure -
 * it does not need to be common to be fatal, because the pool never refills
 * without a reboot.
 */
export function judgeSoak(s: SoakSample): SoakVerdict {
  const mb = (b: number) => `${Math.round(b / (1024 * 1024))} MB`;
  const heap =
    s.heapStartBytes !== null && s.heapEndBytes !== null
      ? ` Heap ${mb(s.heapStartBytes)} → ${mb(s.heapEndBytes)}.`
      : " Heap not measurable on this engine.";

  // Exhaustion FIRST. It is the one failure that is itself a positive
  // measurement of the decoder pool - the panel answered "there is none left",
  // which it can only do if there is a pool at all.
  if (s.decoderExhausted > 0) {
    return {
      pass: false,
      note:
        `🚨 decoder_exhausted on ${s.decoderExhausted} of ${s.cyclesCompleted} cycles. ` +
        "The surface is LEAKING decoders: the pool is single digits and does not " +
        "refill without a reboot." +
        heap,
    };
  }
  // Then: did anything play at all? A run where nothing did measured NOTHING
  // about releasing a decoder, and everything below would be a verdict on an
  // experiment that never happened. Suite B refuses to call a missing clip a
  // decode verdict; this is the same refusal.
  if (s.cyclesPlayed === 0) {
    const codes = Object.entries(s.otherFailures)
      .map(([k, v]) => `${k}×${v}`)
      .join(", ");
    return {
      pass: false,
      note:
        `NOT A RESULT: no cycle ever reached a frame${codes ? ` (${codes})` : ""}, ` +
        "so no decoder was ever acquired and nothing was measured about releasing one. " +
        "Check the soak clip exists before reading this as a leak - or as a pass." +
        heap,
    };
  }
  if (s.cyclesCompleted < s.cyclesRequested) {
    const codes = Object.entries(s.otherFailures)
      .map(([k, v]) => `${k}×${v}`)
      .join(", ");
    return {
      pass: false,
      note:
        `Stopped at ${s.cyclesCompleted} of ${s.cyclesRequested} cycles${
          codes ? ` (${codes})` : ""
        }. No decoder_exhausted, so this is not the leak class - read the codes.` +
        heap,
    };
  }
  if (
    s.heapStartBytes !== null &&
    s.heapEndBytes !== null &&
    s.heapEndBytes > s.heapStartBytes * HEAP_GROWTH_FACTOR &&
    s.heapEndBytes - s.heapStartBytes > HEAP_GROWTH_FLOOR_BYTES
  ) {
    return {
      pass: false,
      note:
        `${s.cyclesCompleted} cycles with no decoder_exhausted, but the heap grew ` +
        `${mb(s.heapEndBytes - s.heapStartBytes)}. Decoders were handed back; something else was not.` +
        heap,
    };
  }
  return {
    pass: true,
    note:
      `${s.cyclesCompleted} start/stop cycles, no decoder_exhausted, memory stable. ` +
      "The release discipline (pause → removeAttribute(src) → load → destroy) holds." +
      heap,
  };
}

// ---------------------------------------------------------------------------
// The polyfill question
// ---------------------------------------------------------------------------

/** Presence of the EME/MSE surfaces Shaka's polyfills patch. */
export type EmeSnapshot = Record<string, boolean>;

export interface PolyfillVerdict {
  required: boolean;
  patched: string[];
  note: string;
}

/**
 * Was `shaka.polyfill.installAll()` still required on this device?
 *
 * Measured rather than assumed: the surfaces Shaka patches are snapshotted
 * before and after the call, and anything that changed from absent to present
 * is a patch this device NEEDED. Nothing changed means the WebView is standards
 * complete here and the call is now a formality - which is a real finding, and
 * the only honest way to ever remove it.
 */
export function polyfillVerdict(
  before: EmeSnapshot,
  after: EmeSnapshot
): PolyfillVerdict {
  const patched = Object.keys(after).filter(
    (k) => after[k] === true && before[k] !== true
  );
  return {
    required: patched.length > 0,
    patched,
    note: patched.length
      ? `Still required: installAll() supplied ${patched.join(", ")}.`
      : "Not required on this device - every surface Shaka patches was already present. Keep the call (it is idempotent); this is the evidence for one day dropping it.",
  };
}

// ---------------------------------------------------------------------------
// Running it
// ---------------------------------------------------------------------------

export interface PlayRequest {
  url: string;
  /**
   * Widevine licence endpoint. `null` means DELIBERATELY WITHHELD - the probe
   * expects a refusal, and a success is the finding.
   */
  licenceUrl?: string | null;
  /** How long to keep watching once playback has begun. */
  watchMs: number;
}

export interface PlayOutcome {
  /** Did a first frame ever arrive? */
  played: boolean;
  /** Was the playhead still advancing when the watch window closed? */
  sustained: boolean;
  startupMs: number | null;
  parkedAtSeconds: number | null;
  durationSeconds: number | null;
  failure: StreamFailure | null;
  /** Short, non-throwing, NEVER a licence token. */
  detail: string;
  /** The key system the CDM actually took, when it says. */
  keySystem?: string | null;
  emeBefore?: EmeSnapshot;
  emeAfter?: EmeSnapshot;
}

/**
 * Everything the orchestration needs from a real decoder.
 *
 * An interface rather than a direct Shaka dependency so the whole run - three
 * suites, every verdict, the whole table - is testable in jsdom against a fake.
 * The real one lives in `PlayerProbe.tsx`, which is the only file here that
 * knows what a `<video>` element is.
 */
export interface ProbeRunner {
  play(req: PlayRequest): Promise<PlayOutcome>;
  /** Ask the CDM down the robustness ladder. `unknown` is a valid answer. */
  securityLevel(): Promise<{ level: SecurityLevel; robustness: string | null }>;
  /** `navigator.requestMediaKeySystemAccess` exists at all. */
  emeAvailable(): boolean;
  /** Widevine answers at all (`drmSupported()`). */
  widevineSupported(): Promise<boolean>;
  /** JS heap size where the engine exposes it; null elsewhere. */
  heapBytes(): number | null;
}

export interface DrmAttempt {
  id: "licensed" | "withheld";
  label: string;
  played: boolean;
  sustained: boolean;
  failure: StreamFailure | null;
  detail: string;
  startupMs: number | null;
  parkedAtSeconds: number | null;
  /** Redacted. Never the token. */
  licence: string;
}

export interface ProbeReport {
  probe: "player";
  startedAt: string;
  finishedAt: string | null;
  phase: string;
  device: Record<string, string | number | undefined>;
  plan: { source: PlanSource; drmAsset: string };
  drm: {
    emeAvailable: boolean;
    widevineSupported: boolean;
    securityLevel: SecurityLevel;
    robustness: string | null;
    securityNote: string;
    polyfill: PolyfillVerdict | null;
    attempts: DrmAttempt[];
    verdict: DrmDiagnosis | null;
    withheld: { pass: boolean; note: string } | null;
    expiry: string;
  };
  ladder: LadderRow[];
  ceiling: CeilingVerdict | null;
  soak: (SoakSample & SoakVerdict) | null;
}

/**
 * Where the ladder in this report came from — printed in the VERDICT block,
 * because a reader has no other way to tell which ladder they are looking at.
 *
 * Declared here, once, rather than inline at each of the three places that used
 * to spell it out: `PlayerProbeScreen` resolves it, `RunOptions` carries it and
 * `ProbeReport` prints it, and a union widened in two of those three is exactly
 * the "two type families for one idea" shape that made the TV menu advertise
 * sections whose pages rendered nothing.
 *
 * `plan_unreachable` is NOT `default`: the ladder is the built-in one either
 * way, but this one means somebody passed `&plan=` and it did not load, which
 * makes an otherwise plausible report the wrong answer to their question.
 */
export type PlanSource = "server" | "default" | "override" | "plan_unreachable";

export interface RunOptions {
  plan: ProbePlan;
  planSource: PlanSource;
  runner: ProbeRunner;
  device: Record<string, string | number | undefined>;
  /** True when the panel actually claims a 4K framebuffer. */
  is4k: boolean;
  /** Called after every step, so the screen fills in as the panel works. */
  onProgress?: (report: ProbeReport) => void;
}

function emptyReport(opts: RunOptions): ProbeReport {
  return {
    probe: "player",
    startedAt: new Date().toISOString(),
    finishedAt: null,
    phase: "starting",
    device: opts.device,
    plan: {
      source: opts.planSource,
      drmAsset: opts.plan.drm.label,
    },
    drm: {
      emeAvailable: false,
      widevineSupported: false,
      securityLevel: "unknown",
      robustness: null,
      securityNote: "",
      polyfill: null,
      attempts: [],
      verdict: null,
      withheld: null,
      expiry: "not run",
    },
    ladder: [],
    ceiling: null,
    soak: null,
  };
}

/**
 * Run all three suites, in order, on one panel.
 *
 * ORDER IS LOAD-BEARING. Suite C (the soak) runs LAST, because it is the one
 * that can exhaust the decoder pool - and a pool exhausted by the soak would
 * make every rung above it report `decoder_exhausted` and look like a decode
 * ceiling that is not there. For the same reason the whole run mounts at most
 * ONE surface at a time and the runner releases it explicitly between attempts.
 *
 * Nothing here throws. A panel that dies mid-suite must still print the rows it
 * already has, because a partial table taken at a bench is worth more than a
 * complete one nobody could obtain.
 */
export async function runPlayerProbe(opts: RunOptions): Promise<ProbeReport> {
  const report = emptyReport(opts);
  const tick = (phase: string) => {
    report.phase = phase;
    opts.onProgress?.({ ...report });
  };

  // ---- capability, before anything is loaded -----------------------------
  tick("asking the device about EME");
  report.drm.emeAvailable = safeBool(() => opts.runner.emeAvailable());
  report.drm.widevineSupported = await safeAsyncBool(() =>
    opts.runner.widevineSupported()
  );
  try {
    const lvl = await opts.runner.securityLevel();
    report.drm.securityLevel = lvl.level;
    report.drm.robustness = lvl.robustness;
  } catch {
    report.drm.securityLevel = "unknown";
    report.drm.robustness = null;
  }
  report.drm.securityNote = judgeSecurityLevel(
    report.drm.securityLevel,
    report.drm.widevineSupported
  ).note;
  tick("EME answered");

  // ---- SUITE A: encrypted DASH, with and without a licence ---------------
  tick("SUITE A · encrypted DASH, licence supplied");
  const licensed = await safePlay(opts.runner, {
    url: opts.plan.drm.manifestUrl,
    licenceUrl: opts.plan.drm.licenceUrl,
    watchMs: Math.max(6_000, opts.plan.drm.expiryWatchMs),
  });
  report.drm.attempts.push({
    id: "licensed",
    label: `${opts.plan.drm.label} · licence supplied`,
    played: licensed.played,
    sustained: licensed.sustained,
    failure: licensed.failure,
    detail: licensed.detail,
    startupMs: licensed.startupMs,
    parkedAtSeconds: licensed.parkedAtSeconds,
    licence: redactLicenceUrl(opts.plan.drm.licenceUrl),
  });
  report.drm.verdict = diagnoseDrm(licensed.played, licensed.failure);
  if (licensed.emeBefore && licensed.emeAfter) {
    report.drm.polyfill = polyfillVerdict(
      licensed.emeBefore,
      licensed.emeAfter
    );
  }
  // The expiry line is a positive claim about licence RENEWAL, so it may only
  // be made when a licence was actually held. A run that never played did not
  // exercise the renewal path at all, and saying "held open with no expiry
  // event" there would assert a pass from a stream that never started.
  report.drm.expiry =
    opts.plan.drm.expiryWatchMs <= 0
      ? "not run - a Verimatrix token lives 5 minutes, so this costs 6. Set drm.expiryWatchMs on the plan (or ?expiry=1) to run it."
      : !licensed.played
        ? `not measured - the licensed attempt never played (${licensed.failure ?? "no code"}), so no licence was ever held to expire.`
        : licensed.failure === "licence_expired"
          ? `licence_expired after ${(licensed.parkedAtSeconds ?? 0).toFixed(1)}s of playback - the renewal filter did not re-mint.`
          : `Held open ${Math.round(opts.plan.drm.expiryWatchMs / 1000)}s with no expiry event: the per-request mint kept the token younger than one round trip.`;
  tick("SUITE A · encrypted DASH, licence WITHHELD");

  const withheld = await safePlay(opts.runner, {
    url: opts.plan.drm.manifestUrl,
    licenceUrl: null,
    watchMs: 6_000,
  });
  report.drm.attempts.push({
    id: "withheld",
    label: `${opts.plan.drm.label} · licence WITHHELD (a refusal is the pass)`,
    played: withheld.played,
    sustained: withheld.sustained,
    failure: withheld.failure,
    detail: withheld.detail,
    startupMs: withheld.startupMs,
    parkedAtSeconds: withheld.parkedAtSeconds,
    licence: "(none - deliberately withheld)",
  });
  report.drm.withheld = judgeWithheld(withheld);
  tick("SUITE A complete");

  // ---- SUITE B: the decode ladder ----------------------------------------
  for (const rung of opts.plan.ladder) {
    tick(`SUITE B · ${rung.label}`);
    if (rung.requires4k && !opts.is4k) {
      report.ladder.push({
        ...rung,
        outcome: "skipped",
        note: "This panel does not claim a 4K framebuffer, so a 4K rung would measure nothing.",
        parkedAtSeconds: null,
        durationSeconds: null,
        startupMs: null,
        failure: null,
        detail: "",
      });
      continue;
    }
    if (!rung.url) {
      report.ladder.push({
        ...rung,
        outcome: "no_clip",
        note: "The plan supplied no clip for this rung.",
        parkedAtSeconds: null,
        durationSeconds: null,
        startupMs: null,
        failure: null,
        detail: "",
      });
      continue;
    }
    const out = await safePlay(opts.runner, {
      url: rung.url,
      watchMs: rung.sustainMs ?? DEFAULT_SUSTAIN_MS,
    });
    const judged = judgeRung(out);
    report.ladder.push({
      ...rung,
      outcome: judged.outcome,
      note: judged.note,
      parkedAtSeconds: out.parkedAtSeconds,
      durationSeconds: out.durationSeconds,
      startupMs: out.startupMs,
      failure: out.failure,
      detail: out.detail,
    });
    report.ceiling = readLadder(report.ladder);
  }
  report.ceiling = readLadder(report.ladder);
  tick("SUITE B complete");

  // ---- SUITE C: the decoder-release soak ---------------------------------
  const sample: SoakSample = {
    cyclesRequested: opts.plan.soak.cycles,
    cyclesCompleted: 0,
    cyclesPlayed: 0,
    decoderExhausted: 0,
    otherFailures: {},
    heapStartBytes: safeHeap(opts.runner),
    heapEndBytes: null,
  };
  for (let i = 0; i < opts.plan.soak.cycles; i++) {
    if (i % 5 === 0) tick(`SUITE C · soak ${i}/${opts.plan.soak.cycles}`);
    const out = await safePlay(opts.runner, {
      url: opts.plan.soak.url,
      watchMs: opts.plan.soak.watchMs,
    });
    sample.cyclesCompleted = i + 1;
    if (out.played) sample.cyclesPlayed += 1;
    if (out.failure === "decoder_exhausted") {
      sample.decoderExhausted += 1;
      // Stop at the first exhaustion. Every later cycle would report the same
      // thing for the same reason, and the panel needs a reboot before any of
      // it means anything again.
      break;
    }
    if (out.failure) {
      sample.otherFailures[out.failure] =
        (sample.otherFailures[out.failure] ?? 0) + 1;
    }
  }
  sample.heapEndBytes = safeHeap(opts.runner);
  report.soak = { ...sample, ...judgeSoak(sample) };

  report.finishedAt = new Date().toISOString();
  tick("done");
  return { ...report };
}

function safeBool(fn: () => boolean): boolean {
  try {
    return fn() === true;
  } catch {
    return false;
  }
}

async function safeAsyncBool(fn: () => Promise<boolean>): Promise<boolean> {
  try {
    return (await fn()) === true;
  } catch {
    return false;
  }
}

function safeHeap(runner: ProbeRunner): number | null {
  try {
    return runner.heapBytes();
  } catch {
    return null;
  }
}

/**
 * One attempt, which may not throw.
 *
 * A runner that rejects would abandon every remaining suite, and the rows we
 * already have are the ones somebody drove to a hotel to obtain. A rejection is
 * therefore a row: `unknown`, with the reason in the detail column, which is
 * exactly the contract the `StreamFailure` union already offers.
 */
async function safePlay(
  runner: ProbeRunner,
  req: PlayRequest
): Promise<PlayOutcome> {
  try {
    const out = await runner.play(req);
    return { ...out, detail: scrubSecrets(out.detail) };
  } catch (err) {
    return {
      played: false,
      sustained: false,
      startupMs: null,
      parkedAtSeconds: null,
      durationSeconds: null,
      failure: "unknown",
      detail: scrubSecrets(
        `runner threw: ${err instanceof Error ? err.message.slice(0, 120) : String(err).slice(0, 120)}`
      ),
    };
  }
}

/**
 * The verdict, in the words `docs/player-hardware-tests.md` asks somebody to
 * write down. Printed at the top of the screen so a photograph of the FIRST
 * fold is already the finding.
 */
export function summarise(report: ProbeReport): string[] {
  const lines: string[] = [];
  const model = report.device.model ?? "unknown model";
  const fw = report.device.firmware ?? "unknown firmware";
  lines.push(`DEVICE: ${model} · firmware ${fw}`);

  if (report.drm.verdict) {
    const v = report.drm.verdict.verdict;
    lines.push(
      `PLAYER-2 (encrypted DASH): ${
        v === "yes"
          ? "YES"
          : v === "no"
            ? "NO - the CDM is absent or refused the key system"
            : v === "never_started"
              ? "INCONCLUSIVE - the player never started"
              : "NOT A DRM ANSWER - see the failure code"
      } · ${report.drm.verdict.meaning}`
    );
  }
  lines.push(
    `WIDEVINE LEVEL: ${report.drm.securityLevel} · ${report.drm.securityNote}`
  );
  if (report.drm.withheld) {
    lines.push(
      `LICENCE WITHHELD: ${report.drm.withheld.pass ? "PASS" : "FAIL"} · ${report.drm.withheld.note}`
    );
  }
  if (report.ceiling) {
    lines.push(
      `PLAYER-4 (decode ceiling): highest passing rung ${
        report.ceiling.highestPassed ?? "none"
      }${
        report.ceiling.firstFailure
          ? `, first failure ${report.ceiling.firstFailure}`
          : ""
      } · ${report.ceiling.meaning}`
    );
    lines.push(
      `ABR CAP: ${
        report.ceiling.abrCapMbps === null
          ? "no passing rung declared a bitrate"
          : `set at or below ${report.ceiling.abrCapMbps} Mbps`
      }`
    );
  }
  if (report.soak) {
    lines.push(
      `DECODER SOAK: ${report.soak.pass ? "PASS" : "FAIL"} · ${report.soak.note}`
    );
  }
  return lines;
}
